import {
  clampTransformForSide,
  getDefaultArtworkTransform,
  getSidePlacementSpec,
} from './merchPlaygroundConfig';

export const STUDIO_PRODUCTS = ['tshirt', 'hoody'];

export const STUDIO_MOCKUP_OPTIONS = {
  tshirt: [
    { id: 'front-flat', label: 'Front flat', view: 'front', variant: 'flat', plateView: 'front', calibrationId: 'front-2' },
    { id: 'back-flat', label: 'Back flat', view: 'back', variant: 'flat', plateView: 'back', calibrationId: 'back-2' },
    { id: 'hanging', label: 'Hanging', view: 'front', variant: 'hanging', plateView: 'hanging-2' },
    { id: 'folded', label: 'Folded', view: 'front', variant: 'folded', plateView: 'folded' },
  ],
  hoody: [
    { id: 'front-flat', label: 'Front flat', view: 'front', variant: 'flat', plateView: 'front', calibrationId: 'front-2' },
    { id: 'back-flat', label: 'Back flat', view: 'back', variant: 'flat', plateView: 'back', calibrationId: 'back-2' },
    { id: 'hanging', label: 'Hanging', view: 'front', variant: 'hanging', plateView: 'hanging-2' },
    { id: 'folded', label: 'Folded', view: 'front', variant: 'folded', plateView: 'folded' },
  ],
};

const GARMENT_LAYOUT = {
  tshirt: {
    width: 420,
    height: 520,
    print: {
      front: { x: 0.232, y: 0.22, w: 0.51, h: 0.60 },
      back: { x: 0.232, y: 0.21, w: 0.51, h: 0.61 },
      sleeveLeft: { x: 0.37, y: 0.35, w: 0.28, h: 0.25 },
      sleeveRight: { x: 0.37, y: 0.35, w: 0.28, h: 0.25 },
      neckLabelInner: { x: 0.41, y: 0.29, w: 0.18, h: 0.12 },
    },
  },
  hoody: {
    width: 420,
    height: 540,
    print: {
      front: { x: 0.29, y: 0.24, w: 0.42, h: 0.58 },
      back: { x: 0.28, y: 0.22, w: 0.44, h: 0.61 },
      sleeveLeft: { x: 0.37, y: 0.34, w: 0.28, h: 0.25 },
      sleeveRight: { x: 0.37, y: 0.34, w: 0.28, h: 0.25 },
      neckLabelInner: { x: 0.41, y: 0.29, w: 0.18, h: 0.11 },
    },
  },
};

const EXPANDED_PRINT = {
  tshirt: {
    front: { x: 0.18, y: 0.16, w: 0.64, h: 0.70 },
    back: { x: 0.18, y: 0.16, w: 0.64, h: 0.70 },
  },
  hoody: {
    front: { x: 0.18, y: 0.18, w: 0.64, h: 0.65 },
    back: { x: 0.18, y: 0.18, w: 0.64, h: 0.65 },
  },
};

export const getGarmentLayout = (productType) => GARMENT_LAYOUT[productType] || GARMENT_LAYOUT.tshirt;

export const getPrintRect = (productType, side, mode = 'standard') => {
  const layout = getGarmentLayout(productType);
  const expanded = EXPANDED_PRINT[productType]?.[side];
  const print = mode === 'expanded' && expanded ? expanded : layout.print[side] || layout.print.front;
  return {
    left: print.x * layout.width,
    top: print.y * layout.height,
    width: print.w * layout.width,
    height: print.h * layout.height,
  };
};

export const getArtworkBox = (productType, side, transform) => {
  const mode = transform?.mode === 'expanded' ? 'expanded' : 'standard';
  const rect = getPrintRect(productType, side, mode);
  const spec = getSidePlacementSpec(productType, side);
  const clamped = clampTransformForSide(productType, side, {
    ...getDefaultArtworkTransform(productType, side),
    ...transform,
  });
  const scale = clamped.scale ?? 1;
  const maxDim = Math.min(rect.width, rect.height);
  const size = maxDim * scale;
  const centerX = rect.left + rect.width / 2 + (clamped.offsetX || 0) * (rect.width / 2);
  const centerY = rect.top + rect.height / 2 + (clamped.offsetY || 0) * (rect.height / 2);
  const minX = rect.left + size / 2;
  const maxX = rect.left + rect.width - size / 2;
  const minY = rect.top + size / 2;
  const maxY = rect.top + rect.height - size / 2;
  const x = mode === 'expanded'
    ? Math.max(rect.left, Math.min(rect.left + rect.width, centerX))
    : minX <= maxX ? Math.max(minX, Math.min(maxX, centerX)) : rect.left + rect.width / 2;
  const y = mode === 'expanded'
    ? Math.max(rect.top, Math.min(rect.top + rect.height, centerY))
    : minY <= maxY ? Math.max(minY, Math.min(maxY, centerY)) : rect.top + rect.height / 2;
  return {
    x: x - size / 2,
    y: y - size / 2,
    width: size,
    height: size,
    scaleMin: spec.scaleMin,
    scaleMax: mode === 'expanded' ? 2.5 : spec.scaleMax,
    offsetX: ((x - (rect.left + rect.width / 2)) / (rect.width / 2)) || 0,
    offsetY: ((y - (rect.top + rect.height / 2)) / (rect.height / 2)) || 0,
    scale,
  };
};

export const offsetFromDrag = (productType, side, transform, deltaX, deltaY, rectScale = 1) => {
  const rect = getPrintRect(productType, side, transform?.mode);
  const box = getArtworkBox(productType, side, transform);
  return {
    offsetX: box.offsetX + (deltaX / rectScale) / (rect.width / 2),
    offsetY: box.offsetY + (deltaY / rectScale) / (rect.height / 2),
    scale: box.scale,
    mode: transform?.mode === 'expanded' ? 'expanded' : 'standard',
  };
};
